'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useDeleteIndividual } from '@/api/hooks/useDeleteIndividual'
import { useMe } from '@/api/hooks/useMe'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'

export const DeleteIndividualDialog = () => {
  const [open, setOpen] = useState(false)
  const { selectedIndividual } = useIndividuals()
  const { data: me } = useMe()
  const { hasProviderBilling } = useSubscription()
  const deleteIndividual = useDeleteIndividual()
  const navigate = useNavigate()
  const schema = useMemo(
    () =>
      z.object({
        name: z.literal(selectedIndividual?.firstName ?? ''),
      }),
    [selectedIndividual],
  )
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(schema),
  })
  const { t } = useTranslation()

  if (!selectedIndividual) return null

  // Deleting belongs to whoever created the individual — the backend refuses it for
  // anyone else. Rendering nothing rather than a disabled button: this takes everyone's
  // documents and errands away at once, and a greyed-out control invites the question of
  // how to get it, when the answer is that a co-owner never should.
  const isAdmin = !!selectedIndividual.users.find(u => u.id === me?.id)?.roles?.includes('admin')
  if (!isAdmin) return null

  const handleSubmit = async () => {
    // Read before the delete: `selectedIndividual` comes off the me-query, which the
    // mutation invalidates, so by the time the toast is built the name is gone.
    const { firstName } = selectedIndividual

    await deleteIndividual.mutateAsync({ individualId: selectedIndividual.id })

    form.reset()
    setOpen(false)
    toast.success(t('dialogs.deleteIndividual.successToast', { name: firstName }))

    // Leaving the user on /settings is what makes a successful delete look like a
    // failed one: useIndividuals auto-selects whoever is left, so the page re-renders
    // fully populated for a different person and invites a second delete. `replace`
    // because the settings page of a deleted person is not a state to go back to.
    navigate({ replace: true, to: '/home' })
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button variant="destructive">{t('global.delete')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dialogs.deleteIndividual.title')}</DialogTitle>
          <DialogDescription>{t('dialogs.deleteIndividual.description')}</DialogDescription>
        </DialogHeader>

        {/* Said before the confirmation, not after. The seat is freed either way, but the
            subscription keeps billing — somebody who deletes a profile expecting the
            charges to stop would otherwise only find out on their next statement. */}
        {hasProviderBilling && (
          <Alert variant="destructive">
            <AlertTitle>{t('dialogs.deleteIndividual.subscriptionWarningTitle')}</AlertTitle>
            <AlertDescription>
              {t('dialogs.deleteIndividual.subscriptionWarningBody', {
                name: selectedIndividual.firstName,
              })}
            </AlertDescription>
          </Alert>
        )}

        <FormProvider {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FieldGroup>
              <Field>
                <FieldLabel>{t('dialogs.deleteIndividual.verifyFirstNameLabel')}</FieldLabel>
                <Input
                  autoComplete="off"
                  placeholder={selectedIndividual.firstName}
                  type="text"
                  {...form.register('name')}
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                )}
              </Field>
            </FieldGroup>

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setOpen(false)
                  form.reset()
                }}
                type="button"
                variant="outline"
              >
                {t('global.cancel')}
              </Button>
              <Button
                disabled={form.formState.isSubmitting}
                isLoading={form.formState.isSubmitting}
                type="submit"
                variant="destructive"
              >
                {t('global.delete')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
